import React, { useState, useEffect } from 'react';
import { Instagram, Twitter, Facebook, ExternalLink } from 'lucide-react';

const SocialMediaFeed = ({ 
  instagramUsername = '',
  twitterUsername = '',
  facebookPageId = '',
  showTitle = true,
  maxPosts = 6 
}) => {
  const [activeTab, setActiveTab] = useState('instagram');
  
  // For Instagram - using embed approach (works without API key)
  const InstagramEmbed = ({ username }) => {
    if (!username) return null;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Instagram doesn't allow direct embedding without their embed script,
            so we show a link to their profile instead */}
        <a
          href={`https://instagram.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="col-span-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-xl p-6 flex items-center justify-between hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center gap-3">
            <Instagram className="h-8 w-8" />
            <div>
              <p className="font-semibold">@{username}</p>
              <p className="text-sm opacity-90">Follow us on Instagram</p>
            </div>
          </div>
          <ExternalLink className="h-5 w-5" />
        </a>
        
        {/* Instagram Widget Placeholder - Would use SnapWidget or similar service */}
        <div className="col-span-full text-center py-8 bg-gray-50 rounded-xl">
          <Instagram className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-sm">Instagram feed widget</p>
          <p className="text-xs text-gray-400 mt-1">Configure in admin settings</p>
        </div>
      </div>
    );
  };

  // Twitter/X Embed
  const TwitterEmbed = ({ username }) => {
    useEffect(() => {
      if (!username) return;
      // Load Twitter widgets script
      const script = document.createElement('script');
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }, [username]);
    
    if (!username) return null;
    
    return (
      <div className="space-y-4">
        <a
          href={`https://twitter.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-black text-white rounded-xl p-6 flex items-center justify-between hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center gap-3">
            <Twitter className="h-8 w-8" />
            <div>
              <p className="font-semibold">@{username}</p>
              <p className="text-sm opacity-90">Follow us on X</p>
            </div>
          </div>
          <ExternalLink className="h-5 w-5" />
        </a>
        
        {/* Twitter Timeline Embed */}
        <div className="bg-white rounded-xl overflow-hidden border">
          <a 
            className="twitter-timeline" 
            data-height="400"
            data-theme="light"
            href={`https://twitter.com/${username}?ref_src=twsrc%5Etfw`}
          >
            Loading tweets...
          </a>
        </div>
      </div>
    );
  };

  // Facebook Embed
  const FacebookEmbed = ({ pageId }) => {
    if (!pageId) return null;
    
    return (
      <div className="space-y-4">
        <a
          href={`https://facebook.com/${pageId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-[#1877f2] text-white rounded-xl p-6 flex items-center justify-between hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center gap-3">
            <Facebook className="h-8 w-8" />
            <div>
              <p className="font-semibold">{pageId}</p>
              <p className="text-sm opacity-90">Follow us on Facebook</p>
            </div>
          </div>
          <ExternalLink className="h-5 w-5" />
        </a>
        
        {/* Facebook Page Plugin */}
        <div className="bg-white rounded-xl overflow-hidden border p-4">
          <iframe
            src={`https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2F${pageId}&tabs=timeline&width=500&height=400&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`}
            width="100%"
            height="400"
            style={{ border: 'none', overflow: 'hidden' }}
            scrolling="no"
            frameBorder="0"
            allow="encrypted-media"
            title="Facebook Feed"
          />
        </div>
      </div>
    );
  };

  const hasAnySocial = instagramUsername || twitterUsername || facebookPageId;

  if (!hasAnySocial) {
    return (
      <div className="bg-gray-50 rounded-2xl p-8 text-center">
        <div className="flex justify-center gap-4 mb-4">
          <Instagram className="h-8 w-8 text-gray-300" />
          <Twitter className="h-8 w-8 text-gray-300" />
          <Facebook className="h-8 w-8 text-gray-300" />
        </div>
        <p className="text-gray-500">No social media accounts configured</p>
        <p className="text-sm text-gray-400 mt-1">Add your social handles in admin settings</p>
      </div>
    );
  }

  const tabs = [];
  if (instagramUsername) tabs.push({ id: 'instagram', label: 'Instagram', icon: Instagram });
  if (twitterUsername) tabs.push({ id: 'twitter', label: 'X / Twitter', icon: Twitter });
  if (facebookPageId) tabs.push({ id: 'facebook', label: 'Facebook', icon: Facebook });

  return (
    <div className="space-y-6">
      {showTitle && (
        <h2 className="text-2xl font-bold text-[#0a0203]">Follow Us</h2>
      )}
      
      {tabs.length > 1 && (
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-[#003456] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      )}
      
      <div>
        {activeTab === 'instagram' && instagramUsername && (
          <InstagramEmbed username={instagramUsername} />
        )}
        {activeTab === 'twitter' && twitterUsername && (
          <TwitterEmbed username={twitterUsername} />
        )}
        {activeTab === 'facebook' && facebookPageId && (
          <FacebookEmbed pageId={facebookPageId} />
        )}
      </div>
    </div>
  );
};

export default SocialMediaFeed;
